import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, Shield, Scale, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function MentionsLegales() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen pt-24 pb-12 bg-gradient-to-b from-zinc-50 to-white" data-testid="mentions-legales-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('footerExt.home')}
            </Button>
          </Link>
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
            {t('legal.title')}
          </h1>
          <p className="text-zinc-600">
            Mentions légales, conditions générales et politique de confidentialité de Brious'O Folies.
          </p>
        </div>

        <Tabs defaultValue="mentions" className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-white shadow-sm h-auto p-1 rounded-xl mb-8">
            <TabsTrigger value="mentions" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white py-3 rounded-lg text-xs sm:text-sm">
              <FileText className="h-4 w-4 mr-1 hidden sm:inline" />
              {t('legal.legalNotice')}
            </TabsTrigger>
            <TabsTrigger value="cgv" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white py-3 rounded-lg text-xs sm:text-sm">
              <Scale className="h-4 w-4 mr-1 hidden sm:inline" />
              {t('legal.cgv')}
            </TabsTrigger>
            <TabsTrigger value="confidentialite" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white py-3 rounded-lg text-xs sm:text-sm">
              <Shield className="h-4 w-4 mr-1 hidden sm:inline" />
              {t('legal.privacy')}
            </TabsTrigger>
            <TabsTrigger value="reglements" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white py-3 rounded-lg text-xs sm:text-sm">
              <AlertCircle className="h-4 w-4 mr-1 hidden sm:inline" />
              Règlements
            </TabsTrigger>
          </TabsList>

          {/* Mentions Légales */}
          <TabsContent value="mentions">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-fuchsia-600" />
                  Mentions Légales
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-zinc max-w-none">
                <h3>Éditeur du site</h3>
                <p>
                  <strong>Association Brious'O Folies</strong><br />
                  Association loi 1901<br />
                  Siège social : Domaine de la Briouse, 86600 Sanxay<br />
                  Téléphone : 07 49 51 30 90
                </p>

                <h3>Directeur de la publication</h3>
                <p>Le Président de l'association Brious'O Folies</p>

                <h3>Hébergement</h3>
                <p>
                  Ce site est hébergé par Emergent Agent<br />
                  Pour toute question technique, contactez-nous via le formulaire de contact.
                </p>

                <h3>Propriété intellectuelle</h3>
                <p>
                  L'ensemble du contenu de ce site (textes, images, vidéos, logos) est la propriété 
                  exclusive de l'association Brious'O Folies ou de ses partenaires. Toute reproduction, 
                  même partielle, est interdite sans autorisation préalable.
                </p>

                <h3>Crédits photos</h3>
                <p>
                  Photos : © Brious'O Folies, Unsplash, et partenaires.<br />
                  Logo : © Brious'O Folies
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CGV */}
          <TabsContent value="cgv">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="h-5 w-5 text-fuchsia-600" />
                  Conditions Générales de Vente
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-zinc max-w-none">
                <h3>Article 1 - Objet</h3>
                <p>
                  Les présentes conditions générales de vente régissent les inscriptions aux événements 
                  et activités organisés par l'association Brious'O Folies.
                </p>

                <h3>Article 2 - Inscriptions</h3>
                <p>
                  Les inscriptions aux événements se font en ligne via notre partenaire HelloAsso ou 
                  BilletWeb. Le paiement vaut acceptation des présentes conditions.
                </p>

                <h3>Article 3 - Tarifs</h3>
                <p>
                  Les tarifs sont indiqués en euros TTC. L'association se réserve le droit de modifier 
                  ses tarifs à tout moment, mais les événements seront facturés au tarif en vigueur au 
                  moment de l'inscription.
                </p>

                <h3>Article 4 - Annulation et remboursement</h3>
                <p>
                  <strong>Par le participant :</strong> Toute annulation doit être signalée au moins 
                  48h avant l'événement pour un remboursement intégral. Passé ce délai, aucun 
                  remboursement ne sera effectué.
                </p>
                <p>
                  <strong>Par l'association :</strong> En cas d'annulation d'un événement par 
                  l'association (intempéries, cas de force majeure), les participants seront 
                  intégralement remboursés ou pourront reporter leur inscription.
                </p>

                <h3>Article 5 - Responsabilité</h3>
                <p>
                  Les participants s'engagent à respecter les consignes de sécurité et le règlement 
                  intérieur du Domaine de la Briouse. L'association décline toute responsabilité en 
                  cas de non-respect de ces règles.
                </p>

                <h3>Article 6 - Assurance</h3>
                <p>
                  Pour les activités équestres, les participants doivent être titulaires d'une licence 
                  FFE en cours de validité ou souscrire une assurance journalière proposée sur place.
                </p>

                <h3>Article 7 - Droit à l'image</h3>
                <p>
                  En participant à nos événements, vous autorisez l'association à utiliser les photos 
                  et vidéos prises lors de ces événements à des fins de communication.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Politique de confidentialité */}
          <TabsContent value="confidentialite">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-fuchsia-600" />
                  Politique de Confidentialité
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-zinc max-w-none">
                <h3>Collecte des données</h3>
                <p>
                  L'association Brious'O Folies collecte des données personnelles dans le cadre de :
                </p>
                <ul>
                  <li>L'inscription à nos événements</li>
                  <li>L'adhésion à l'association</li>
                  <li>L'inscription à notre newsletter</li>
                  <li>Les demandes de contact</li>
                </ul>

                <h3>Utilisation des données</h3>
                <p>
                  Vos données sont utilisées exclusivement pour :
                </p>
                <ul>
                  <li>Gérer vos inscriptions aux événements</li>
                  <li>Vous envoyer des informations sur nos activités (si vous avez accepté)</li>
                  <li>Répondre à vos demandes</li>
                  <li>Améliorer nos services</li>
                </ul>

                <h3>Conservation des données</h3>
                <p>
                  Vos données sont conservées pendant la durée de votre adhésion et 3 ans après votre 
                  dernière interaction avec l'association.
                </p>

                <h3>Vos droits</h3>
                <p>
                  Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement 
                  et de portabilité de vos données. Pour exercer ces droits, contactez-nous via notre 
                  formulaire de contact.
                </p>

                <h3>Cookies</h3>
                <p>
                  Ce site utilise des cookies techniques nécessaires à son fonctionnement. Aucun cookie 
                  de tracking ou publicitaire n'est utilisé.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Règlements */}
          <TabsContent value="reglements">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-fuchsia-600" />
                  Règlements des Événements
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-zinc max-w-none">
                <h3>Règlement Randonnées Équestres</h3>
                <ul>
                  <li>Licence FFE obligatoire ou assurance journalière</li>
                  <li>Casque homologué obligatoire</li>
                  <li>Niveau minimum Galop 3 requis</li>
                  <li>Respect des allures imposées par le guide</li>
                  <li>Les chevaux doivent être à jour de leurs vaccinations</li>
                </ul>

                <h3>Règlement Courses d'Endurance</h3>
                <ul>
                  <li>Inscription via FFE Compet obligatoire</li>
                  <li>Respect du règlement fédéral FFE</li>
                  <li>Contrôle vétérinaire obligatoire</li>
                  <li>Carnet de santé du cheval à jour</li>
                </ul>

                <h3>Règlement Tombola</h3>
                <ul>
                  <li>Tirage au sort effectué devant huissier</li>
                  <li>Les lots non réclamés sous 3 mois seront remis en jeu</li>
                  <li>Un seul lot par participant</li>
                  <li>Les membres du bureau ne peuvent pas participer</li>
                </ul>

                <h3>Règlement Intérieur du Domaine</h3>
                <ul>
                  <li>Respect des animaux et de l'environnement</li>
                  <li>Interdiction de fumer dans les écuries</li>
                  <li>Les chiens doivent être tenus en laisse</li>
                  <li>Stationnement uniquement sur les zones prévues</li>
                  <li>Les enfants restent sous la responsabilité de leurs parents</li>
                </ul>

                <h3>Documents téléchargeables</h3>
                <p>
                  <a href="/documents/reglement-randonnee.pdf" className="text-fuchsia-600 hover:text-fuchsia-700">
                    📄 Règlement complet Randonnées (PDF)
                  </a><br />
                  <a href="/documents/bulletin-participation.pdf" className="text-fuchsia-600 hover:text-fuchsia-700">
                    📄 Bulletin de participation type (PDF)
                  </a><br />
                  <a href="/documents/autorisation-parentale.pdf" className="text-fuchsia-600 hover:text-fuchsia-700">
                    📄 Autorisation parentale mineurs (PDF)
                  </a>
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
